import dagre from '@dagrejs/dagre';
import type { EntityNode, OwnershipEdge } from './types';

// Locked dims so dagre lays out without a measure pass. The +2 on height
// absorbs the 1px top/bottom border that border-box counts against content;
// without it the badge clips against the bottom border. If EntityPill's box
// changes, update these too.
export const ENTITY_NODE_WIDTH = 240;
export const ENTITY_NODE_HEIGHT = 62;

// One gap for every rank pair, so every edge is drawn over the same vertical
// distance. Sized to clear the % chip (~30px tall, centred on the edge) with
// room to spare above and below it.
const RANKSEP = 112;

// Dagre routes an edge that skips ranks through a dummy node on every rank it
// crosses; those are the only points we keep. The handle endpoints come from
// xyflow, and adjacent-rank edges need no waypoints at all.
const RANK_PITCH = ENTITY_NODE_HEIGHT + RANKSEP;

function intermediateRankPoints(
  points: { x: number; y: number }[],
  sourceY: number,
  targetY: number,
): { x: number; y: number }[] {
  return points.filter(
    (p) =>
      Math.abs(p.y - sourceY) >= RANK_PITCH - 1 &&
      Math.abs(p.y - targetY) >= RANK_PITCH - 1,
  );
}

// Returns nodes with positions filled in and edges annotated with the
// intermediate waypoints dagre routed them through. OwnershipEdge draws its
// own curve from the handle coords through those waypoints.
export function layoutGraph(
  nodes: EntityNode[],
  edges: OwnershipEdge[],
): { nodes: EntityNode[]; edges: OwnershipEdge[] } {
  if (nodes.length === 0) return { nodes, edges };

  // Fresh graph per call — sharing a module-level instance leaks stale
  // positions on data-set swap. Re-creation cost is negligible at our N.
  // Multigraph so parallel share-class edges each get their own routing.
  const g = new dagre.graphlib.Graph({ multigraph: true }).setDefaultEdgeLabel(
    () => ({}),
  );
  // tight-tree ranker keeps direct children on the same rank as their siblings.
  g.setGraph({
    rankdir: 'TB',
    ranker: 'tight-tree',
    nodesep: 64,
    ranksep: RANKSEP,
  });

  for (const n of nodes) {
    g.setNode(n.id, { width: ENTITY_NODE_WIDTH, height: ENTITY_NODE_HEIGHT });
  }
  for (const e of edges) {
    g.setEdge(e.source, e.target, {}, e.id);
  }

  dagre.layout(g);

  const positionedNodes = nodes.map((n) => {
    const { x, y } = g.node(n.id);
    // dagre → center; ReactFlow → top-left.
    return {
      ...n,
      position: {
        x: x - ENTITY_NODE_WIDTH / 2,
        y: y - ENTITY_NODE_HEIGHT / 2,
      },
      // The minimap sizes nodes from these; a read-only chart never gets the measured size back.
      initialWidth: ENTITY_NODE_WIDTH,
      initialHeight: ENTITY_NODE_HEIGHT,
    };
  });

  const routedEdges = edges.map((e) => {
    const waypoints = intermediateRankPoints(
      g.edge(e.source, e.target, e.id).points,
      g.node(e.source).y,
      g.node(e.target).y,
    );
    if (waypoints.length === 0 || !e.data) return e;
    return { ...e, data: { ...e.data, waypoints } };
  });

  return { nodes: positionedNodes, edges: routedEdges };
}
